import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

interface EntityHeroProps {
  /** Photo or logo (sized by the hero). */
  media: ReactNode;
  mediaShape?: "round" | "square";
  title: string;
  /** Short badges shown next to the title (type, activity, roles…). */
  chips?: ReactNode;
  /** One or two muted lines under the title (origin, founding, parent…). */
  meta?: ReactNode;
  /** Follow/share buttons and quick external links. */
  actions?: ReactNode;
  /** Bio / profile, clamped by the caller. */
  body?: ReactNode;
  /** Genres, aliases or other tag-like lines. */
  footer?: ReactNode;
  /** About cards: a right rail on wide screens, a row below on smaller ones. */
  aside?: ReactNode;
}

/**
 * Compact identity band shared by artist and company pages. Everything that
 * used to stack above the discography fits in one band so the first row of
 * covers lands within the first viewport.
 */
export function EntityHero({
  media,
  mediaShape = "round",
  title,
  chips,
  meta,
  actions,
  body,
  footer,
  aside,
}: EntityHeroProps) {
  return (
    <header className="mb-6 grid grid-cols-[5rem_minmax(0,1fr)] items-center gap-x-4 gap-y-4 md:mb-8 md:grid-cols-[8.5rem_minmax(0,1fr)] md:items-start md:gap-x-8 xl:grid-cols-[8.5rem_minmax(0,1fr)_20rem]">
      <div
        className={cn(
          "relative size-20 overflow-hidden bg-secondary md:row-span-2 md:size-[8.5rem]",
          mediaShape === "round" ? "rounded-full" : "rounded-xl",
        )}
      >
        {media}
      </div>

      <div className="min-w-0 space-y-2">
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-x-3.5">
          <h1 className="text-[1.625rem] font-black leading-[1.05] tracking-tight md:text-[2.75rem]">
            {title}
          </h1>
          {chips}
        </div>
        {meta}
      </div>

      <div className="col-span-2 min-w-0 space-y-3 md:col-span-1 md:col-start-2">
        {actions && (
          <div className="-mx-4 flex items-center gap-2 overflow-x-auto px-4 no-scrollbar md:mx-0 md:flex-wrap md:overflow-visible md:px-0">
            {actions}
          </div>
        )}
        {body && <div className="max-w-3xl text-[15px]">{body}</div>}
        {footer}
      </div>

      {aside && (
        <div className="col-span-2 min-w-0 xl:col-span-1 xl:col-start-3 xl:row-span-2 xl:row-start-1">
          {aside}
        </div>
      )}
    </header>
  );
}
